<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    @hide="onDialogHide"
  >
    <q-card class="q-dialog-plugin">
      <q-card-section class="row">
        <div
          class="text-h6 ellipsis col"
        >
          {{ title }}
        </div>
        <q-btn
          v-close-popup
          flat
          round
          dense
          icon="close"
        />
      </q-card-section>
      <q-card-section class="row items-center justify-center q-gutter-md">
        <div
          v-if="!disableClient"
          class="col-12"
        >
          <client-selector
            return-value
            ignore-current-client
            :default-value="defaultClient"
            :hide-clients="hideClients"
            :hide-staff-members="hideStaffMembers"
            outline
            @input="onSelectClient"
          />
        </div>
        <div
          class="col-12"
        >
          <q-select
            v-model="participantSession"
            :options="participantSessionsList"
            :label="$t('sessions.modals.addGroupParticipant.labelSession')"
            data-e2e="dropdown-participant-session"
            :disable="!participantClient"
            emit-value
            map-options
            option-value="id"
            option-label="sessionName"
            :option-disable="option => !option.hasActiveProgram"
            outlined
            :loading="isLoadingParticipantSessions"
          >
            <template #option="scope">
              <q-item v-bind="scope.itemProps">
                <q-item-section>
                  <q-tooltip
                    v-if="!scope.opt.hasActiveProgram"
                  >
                    {{ $t('run.overview.noProgramsText') }}
                  </q-tooltip>
                  {{ scope.opt.sessionName }}
                </q-item-section>
              </q-item>
            </template>

            <template #no-option>
              <q-item
                v-if="!isLoadingParticipantSessions"
              >
                <q-item-section>
                  <q-item-label>
                    {{ $t('sessions.modals.addGroupParticipant.noSessionsAvailable') }}
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </q-select>
        </div>
        <div
          v-if="!hideUser"
          class="col-12"
        >
          <client-users-selector
            v-model="participantUser"
            :client="participantClient?.id"
            :readonly="!participantClient"
            :session-id="participantSession"
            lazy-load
            :disable="!participantSession"
          />
        </div>
      </q-card-section>
      <!-- buttons example -->
      <q-card-actions class="row">
        <q-space />
        <q-btn
          color="secondary"
          :label="$t('common.buttonCancel')"
          flat
          @click="onCancelClick"
        />
        <q-btn
          color="primary"
          :label="$t('common.buttonAdd')"
          :disable="!canSubmit"
          flat
          data-e2e="button-add-participant"
          @click="onOKClick"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
<script>

import ClientSelector from 'components/ClientSelector';
import ClientUsersSelector from 'components/UI/ClientUsersSelector';
import { chain, size } from 'lodash';
import { mapState } from 'pinia';
import { ACTIVE_PROGRAM_STATUSES } from 'src/constants/programs';
import ProgramsDB from 'src/fb/programs-db';
import SessionProgramsDB from 'src/fb/sessionPrograms-db';
import SessionsDB from 'src/fb/sessions-db';
import { auth } from 'src/store/auth';

export default {
  name: 'AddSessionDialog',

  components: {
    ClientSelector,
    ClientUsersSelector,
  },

  props: {
    title: {
      type: String,
      required: true,
    },
    defaultClient: {
      type: Object,
      default: null,
    },
    hideClients: {
      type: Boolean,
      default: false,
    },
    hideStaffMembers: {
      type: Boolean,
      default: false,
    },
    hideUser: {
      type: Boolean,
      default: false,
    },
    disableClient: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['hide', 'ok'],

  data() {
    return {
      isLoadingParticipantSessions: false,
      participantClient: null,
      participantSession: null,
      participantUser: null,
      participantSessionsList: [],
      participantClientPrograms: [],
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'hasSupervisorCredentials']),

    canSubmit() {
      return this.participantClient && this.participantSession && (this.participantUser || !this.hideUser);
    },

  },

  methods: {

    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      this.$refs.dialog.show();
      this.initDefaults();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    async onOKClick() {
      this.$emit('ok', {
        client: this.participantClient.id,
        clientName: this.participantClient.clientName,
        originalSession: this.participantSession,
        sessionName: chain(this.participantSessionsList)
          .find({ id: this.participantSession })
          .get('sessionName')
          .value(),
        user: this.participantUser?.user || null,
        userName: this.participantUser?.userName || null,
      });
      this.hide();
    },

    onCancelClick() {
      this.hide();
    },

    onSelectClient(client) {
      this.participantClient = client;
      this.loadParticipantSessions();
      this.loadParticipantPrograms();
    },

    async loadParticipantSessions() {
      this.participantSession = null;
      if (!this.participantClient?.id) {
        this.participantSessionsList = [];
        return;
      }
      const sessionsDb = new SessionsDB();
      this.isLoadingParticipantSessions = true;
      try {
        const sessions = await sessionsDb.readAll(
          [
            ['clinic', '==', this.userDetails.clinic],
            ['client', '==', this.participantClient.id],
            ['status', '==', 'scheduled'],
          ],
        );
        this.participantSessionsList = chain(sessions)
          .reject({ isDeleted: true })
          .filter((session) => ((session.sessionType || 'normal') === 'normal'))
          .filter(
            (session) => (
              !session.limitedVisibility
            || this.hasSupervisorCredentials
            || (session.visibility.includes(this.userDetails.userPrivilege || this.userDetails.role))),
          )
          .sortBy('sessionName')
          .value();

        this.participantSessionsList = await this.addToSessionHasActiveProgramField();

        if (size(this.participantSessionsList) === 1) {
          this.participantSession = chain(this.participantSessionsList).head().get('id').value();
        }
        this.participantUser = {
          user: this.userDetails.userId,
          userName: this.userDetails.name,
          role: this.userDetails.role,
          userPrivilege: this.userDetails.userPrivilege || null,
          disable: false,
        };
      } finally {
        this.isLoadingParticipantSessions = false;
      }
    }, // loadParticipantSessions()

    initDefaults() {
      if (this.defaultClient !== null) {
        this.onSelectClient(this.defaultClient);
      }
    },

    async loadParticipantPrograms() {
      if (!this.participantClient?.id) {
        this.participantSessionsList = [];
        return;
      }

      const programsDb = new ProgramsDB();
      this.participantClientPrograms = await programsDb.readAll(
        [
          ['client', '==', this.participantClient.id],
          ['isDeleted', '==', false],
        ],
      );
    }, // loadParticipantPrograms

    async loadParticipantSessionActivePrograms(sessionId) {
      const sessionProgramsDb = new SessionProgramsDB(sessionId);
      const sessionPrograms = await sessionProgramsDb
        .readWithData('order', 'asc', this.participantClient.id);

      const sessionProgramsIds = sessionPrograms.map((sessionProgram) => sessionProgram.program);
      const programsIds = this.participantClientPrograms.reduce((acc, { id, status }) => {
        if (ACTIVE_PROGRAM_STATUSES.includes(status)) {
          acc.push(id);
        }
        return acc;
      }, []);

      const activeSessionPrograms = sessionProgramsIds.filter((id) => programsIds.includes(id));

      return activeSessionPrograms.length;
    }, // loadParticipantSessionActivePrograms()

    async addToSessionHasActiveProgramField() {
      const updateSessionAction = this.participantSessionsList.map(async (session) => {
        const numberOfActivePrograms = await this.loadParticipantSessionActivePrograms(session.id);
        return {
          ...session,
          hasActiveProgram: !!numberOfActivePrograms,
        };
      });

      const updatedSessionsList = await Promise.all(updateSessionAction);
      return updatedSessionsList;
    }, // addToSessionHasActiveProgramField

  },
};
</script>
